import { useState, useEffect } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { EntitiesAPI } from '../api/entities.api';
import { getSinkDatasetId } from 'Internals/pipes';

import type { FlowInputOutput } from '../types/flows.types';

const searchEntity = (subUrl: string, token: string, datasetId: string, entityId: string) =>
  EntitiesAPI.lookupEntity({ subUrl, token, dataset: datasetId }, entityId);

const getLatestEntity = (subUrl: string, token: string, datasetId: string) =>
  EntitiesAPI.fetchEntities(
    { subUrl, token, dataset: datasetId },
    { deleted: false, history: false, limit: 1, reverse: true }
  );

export const useFlowEntities = (
  flow: FlowInputOutput[] | null | undefined,
  subUrl: string,
  token: string,
  previewPipe: (pipe: any, entity: any) => Promise<any>
): any[] => {
  const [entities, setEntities] = useState<any[]>([]);

  useEffect(() => {
    const getFlowEntities = async (objectList: any[]) => {
      let entities: any[] = [];
      let firstPipeEncountered = false;
      for (const obj of objectList) {
        if (get(obj, 'config.effective.type') !== 'pipe') {
          entities = entities.concat(null);
          continue;
        }
        const pipe = obj;
        const sinkDatasetId = getSinkDatasetId(pipe);
        const lastEntity = entities[entities.length - 1];
        if (sinkDatasetId) {
          if (!firstPipeEncountered) {
            const firstEntity = (await getLatestEntity(subUrl, token, sinkDatasetId))[0];
            entities = entities.concat(firstEntity);
            firstPipeEncountered = true;
            continue;
          }
          let currentResult;
          try {
            if (!lastEntity || isEmpty(lastEntity)) break;
            const previewResponse = await previewPipe(pipe.config.original, lastEntity);
            currentResult = get(previewResponse, 'transformed[0][0]');
            if (!currentResult) currentResult = get(previewResponse, 'sink[0]');
          } catch {
            const searchResult = await searchEntity(subUrl, token, sinkDatasetId, lastEntity._id);
            currentResult = searchResult[0];
          }
          entities = entities.concat(currentResult);
        } else {
          try {
            const previewResponse = await previewPipe(pipe.config.original, lastEntity);
            let entity = previewResponse.sink[0];
            if (!entity) entity = previewResponse.source;
            entities = entities.concat(entity);
          } catch {
            // entity unavailable, skip
          }
        }
      }
      setEntities(entities);
    };

    const objectList = (flow ?? []).map((c) => c.data);
    getFlowEntities(objectList).catch(() => {});
  }, [flow, subUrl, token, previewPipe]);

  return entities;
};
